import { Checkbox, Table } from '@statsbomb/kitbag-components';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { showRankAtom, tableIdAtom, tableSelectedRowIdsAtom } from '@/atoms/table';
import {
  calculate360MetricKeys,
  calculatePhysicalMetricKeys,
  resolveCellTooltip,
} from '@/components/tables/AggsTable.utils';
import { AGGS_TABLE_CONFIG, LOCKED_COLUMN_BY_TABLE_ID } from '@/consts/dataLocker/table';
import { METRIC_KEY } from '@/consts/searchKeys';
import { useSortTable } from '@/hooks/useSortTable';
import { Entity } from '@/types/entity';
import { MetricDefinition } from '@/types/metric';
import { StatusTranslationKey } from '@/types/status';
import { EnrichedTableRow } from '@/utils/atoms/enrichTableData';
import { getMetricKeyFromColumnKey } from '@/utils/metrics';
import {
  getDataType,
  getHighlightedColumnIndex,
  getLockedStickyColumns,
  getValidColumnKey,
  isLowerBetter,
  isMetricColumn,
} from '@/utils/table';
import { getTranslationColumns } from '@/utils/translations';
import { ContentState } from '../contentState/ContentState';
import { MetricTooltip } from '../metricTooltip/MetricTooltip';
import { NavigateToVideoButton } from '../video/NavigateToVideoButton';
import { CellValueToDisplay } from './CellValueToDisplay';
import { PhysicalMetricHeader } from './PhysicalMetricHeader';

// This is a number selected with product to recommend users filter further before relying on rank
const MAX_ENTRIES_TO_RANK = 500;

export const AggsTable = ({
  data = [],
  isLoading = false,
  visibleColumns = [],
  entity,
  hasRetrievedAllAggs = false,
  withVideo = false,
  metricData = [],
  withSelectedRows = false,
  totalCount,
}: {
  data?: EnrichedTableRow[];
  isLoading?: boolean;
  visibleColumns?: string[];
  entity: Entity;
  hasRetrievedAllAggs?: boolean;
  withVideo?: boolean;
  metricData?: MetricDefinition[];
  withSelectedRows?: boolean;
  totalCount?: number | null;
}) => {
  const { t } = useTranslation('events');
  const { orderBy, handleSort, getSortedState } = useSortTable(hasRetrievedAllAggs);
  const [searchParams] = useSearchParams();
  const { param, entityKey } = AGGS_TABLE_CONFIG[entity];
  const entityId = searchParams.get(param);

  const visibleColumnsSortKeys = visibleColumns.map(key => getValidColumnKey(key));
  const sortedColumnIndex = orderBy ? visibleColumnsSortKeys.findIndex(key => key === orderBy) : -1;
  const isCheckboxColumnVisible = withSelectedRows;
  const highlightedColumnIndex = getHighlightedColumnIndex(sortedColumnIndex, isCheckboxColumnVisible);

  const selectedRowIndex = data.findIndex(obj => String(obj[entityKey].value) === entityId);
  const selectedRows = [selectedRowIndex + 1];

  const showRank = useAtomValue(showRankAtom);
  const tableId = useAtomValue(tableIdAtom);
  const hasLockedColumn = tableId ? !!LOCKED_COLUMN_BY_TABLE_ID[tableId] : false;
  const stickyColumns = hasLockedColumn ? getLockedStickyColumns(withSelectedRows) : undefined;

  const hasData = !isLoading && data.length > 0;
  const threeSixtyMetricKeys = calculate360MetricKeys(metricData);
  const physicalMetricKeys = calculatePhysicalMetricKeys(metricData);
  const [selectedRowIds, setSelectedRowIds] = useAtom(tableSelectedRowIdsAtom);
  const currentRowIds = data.map(row => String(row[entityKey].value));
  const selectedCountInData = currentRowIds.filter(id => selectedRowIds.includes(id)).length;
  const isAllSelected = hasData && currentRowIds.length > 0 && selectedCountInData === currentRowIds.length;
  const isOverLimit = (totalCount ?? 0) > MAX_ENTRIES_TO_RANK;

  const handleRowSelection = (rowId: string) => {
    const newSelection = selectedRowIds.includes(rowId)
      ? selectedRowIds.filter(id => id !== rowId)
      : [...selectedRowIds, rowId];
    setSelectedRowIds(newSelection);
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedRowIds(prev => prev.filter(id => !currentRowIds.includes(id)));
    } else {
      setSelectedRowIds(prev => [...new Set([...prev, ...currentRowIds])]);
    }
  };

  return (
    <>
      <div className={hasData ? 'overflow-x-auto' : 'overflow-x-hidden'}>
        <Table
          isHeadSticky
          highlightedColumn={highlightedColumnIndex}
          selectedRows={selectedRows}
          stickyColumns={stickyColumns}
        >
          <Table.Head>
            <Table.Row>
              {withSelectedRows && (
                <Table.HeaderCell size="regular">
                  <Checkbox
                    id="select-all-checkbox"
                    label={t('selectAll', { ns: 'general' })}
                    withLabel={false}
                    checked={isAllSelected}
                    indeterminate={selectedRowIds.length > 0 && !isAllSelected}
                    onChange={handleSelectAll}
                    disabled={!hasData}
                    size="small"
                  />
                </Table.HeaderCell>
              )}
              {visibleColumnsSortKeys.map((key, index) => {
                const { translationKey, translationNs } = getTranslationColumns(key);
                const columnKey = visibleColumns[index];
                const formatRule = data?.[0]?.[columnKey]?.formatRule ?? 'string';

                const isPhysical =
                  isMetricColumn(columnKey) && physicalMetricKeys.has(getMetricKeyFromColumnKey(columnKey));

                return (
                  <Table.HeaderCell
                    key={key}
                    dataType={getDataType(formatRule)}
                    sortCb={() => handleSort(key, isLowerBetter(formatRule))}
                    sortState={getSortedState(key)}
                    size="regular"
                    wrapText={isMetricColumn(columnKey)}
                  >
                    {isPhysical ? (
                      <PhysicalMetricHeader
                        metricKey={key}
                        translationNs={translationNs}
                        label={t(translationKey, { ns: translationNs })}
                        testId={`column-${index}`}
                      />
                    ) : (
                      <MetricTooltip metricKey={key} translationNs={translationNs}>
                        <span data-testid={`column-${index}`}>{t(translationKey, { ns: translationNs })}</span>
                      </MetricTooltip>
                    )}
                  </Table.HeaderCell>
                );
              })}
            </Table.Row>
          </Table.Head>
          {hasData && (
            <Table.Body>
              {data.map(row => {
                const rowId = String(row[entityKey].value);
                const isRowSelected = selectedRowIds.includes(rowId);
                return (
                  <Table.Row key={rowId} className="[&:hover_.invisible]:visible [&:focus-within_.invisible]:visible">
                    {withSelectedRows && (
                      <Table.DataCell>
                        <Checkbox
                          id={`checkbox-${rowId}`}
                          label={`${t('selectRow', { ns: 'general' })} ${rowId}`}
                          withLabel={false}
                          checked={isRowSelected}
                          onChange={() => handleRowSelection(rowId)}
                          size="small"
                        />
                      </Table.DataCell>
                    )}
                    {visibleColumns.map(columnKey => {
                      const metricKey = isMetricColumn(columnKey) ? getMetricKeyFromColumnKey(columnKey) : undefined;
                      const isPhysical = metricKey ? physicalMetricKeys.has(metricKey) : false;
                      const path =
                        metricKey && !isPhysical
                          ? `${METRIC_KEY}=${metricKey}&${param}=${row[entityKey].value}`
                          : undefined;

                      const cellData = row[columnKey];
                      const { value, formatRule, rank } = cellData;
                      const hasNoRank = rank === null || rank === undefined;
                      const dataPill =
                        isMetricColumn(columnKey) && showRank && totalCount
                          ? {
                              disabled: isOverLimit || hasNoRank,
                              rank: rank ?? 0,
                              total: totalCount,
                              ...(isOverLimit && { label: t('rankDisabled', { ns: 'table' }) }),
                            }
                          : undefined;

                      const { tooltipContent, showAsteriskWithTooltip } = resolveCellTooltip(
                        t,
                        metricKey,
                        threeSixtyMetricKeys,
                        row,
                        'no360Data',
                        { isPhysical, value },
                      );
                      const key = `${rowId}-${columnKey}`;
                      return (
                        <CellValueToDisplay
                          columnKey={columnKey}
                          dataPill={dataPill}
                          formatRule={formatRule}
                          key={key}
                          previewLinkParams={path}
                          showAsteriskWithTooltip={showAsteriskWithTooltip}
                          showNullAsNa
                          testId={key}
                          tooltipContent={tooltipContent}
                          value={value}
                        >
                          {/* Do not show the play button for columns with no video or cells which have no value or value of zero */}
                          {withVideo && isMetricColumn(columnKey) && !!value && !isPhysical && (
                            <NavigateToVideoButton
                              linkParams={`/preview?${path}`}
                              metricKey={getMetricKeyFromColumnKey(columnKey)}
                            />
                          )}
                        </CellValueToDisplay>
                      );
                    })}
                  </Table.Row>
                );
              })}
            </Table.Body>
          )}
        </Table>
      </div>
      {!isLoading && !hasData && <ContentState status={StatusTranslationKey.NO_DATA} />}
      {isLoading && <ContentState status={StatusTranslationKey.LOADING} />}
    </>
  );
};
